// /events（側欄「事件」；舊 /vms/events 轉過來）— 帳號 VMS 事件中心（ls-loop MV.2，docs/mycam/mv-spec.md ③）。
// 帳號底下所有攝影機的事件，伺服器端合併 / 篩選 / 分頁（GET /v3/me/events）：
//   篩選：攝影機、AI 標籤（人 / 寵物 / 車 / 包裹 / 可能誤報 / 無法確定 / 沒判斷過）、事件類型、時段；
//   每個選項旁是筆數（facets：每一維套其他維的篩選）；每小時長條看事件集中在哪。
//   點事件 → 多台同步回放停在事件前 5 秒。
//   即時：時段是「近 N」時，收到 room 事件就重抓第一頁（2 秒內合併一次）。
// 單台 / 分組的事件流照舊在 /events（EventsPage）；這頁是跨攝影機查找用。

import { MuteRowActions, MutedHiddenLine } from '../events/MuteZoneUi';
import { isMuted } from '../events/muteZones';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { camZone, dayWindowInZone, sharedZone, todayInZone } from '../../lib/camTz';
import { eventAiLine } from '../../lib/eventAi';
import { formatTimestampUs } from '../../lib/format';
import {
  fetchMyEvents,
  hourBars,
  MY_EVENT_LABELS,
  playbackHref,
  rangeWindow,
  type MyEvent,
  type MyEventFacets,
  type RangeKey,
} from '../../lib/vms/myEvents';
import { cameraClass, deviceName } from '../devices/deviceModel';
import { useDeviceList } from '../devices/useDeviceList';
import { EventThumb } from '../events/EventTimeline';
import { clusterOfEvent, isUnconfirmed } from '../events/eventsLib';
import { ensureEventRooms, onLiveEvent } from '../events/liveEvents';
import '../events/events.css';
import './vms.css';

const PAGE = 50;
const LIVE_DEBOUNCE_MS = 2000;
const EMPTY_FACETS: MyEventFacets = { device: {}, label: {}, type: {}, hour: {} };

function toggle(xs: string[], x: string): string[] {
  return xs.includes(x) ? xs.filter((y) => y !== x) : [...xs, x];
}

// 篩選條件存 sessionStorage：點事件進回放、再按返回時元件重新掛載，篩選不會被清掉
const FILTER_KEY = 'ls.eventCenter.filters';
interface SavedFilters { range: RangeKey; date: string; devices: string[]; labels: string[]; types: string[] }
function loadFilters(): Partial<SavedFilters> {
  try {
    return JSON.parse(sessionStorage.getItem(FILTER_KEY) ?? '{}') as Partial<SavedFilters>;
  } catch {
    return {};
  }
}

export default function EventCenterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { devices } = useDeviceList();
  const cameras = useMemo(() => (devices ?? []).filter((d) => cameraClass(d) === 'camera'), [devices]);
  const camIds = useMemo(() => cameras.map((d) => d.device_id), [cameras]);
  const nameOf = useMemo(() => new Map(cameras.map((d) => [d.device_id, deviceName(d)])), [cameras]);
  const zone = sharedZone(camIds);

  const [saved] = useState(loadFilters);
  const [range, setRange] = useState<RangeKey>(saved.range ?? '24h');
  const [date, setDate] = useState(() => saved.date ?? todayInZone(zone));
  const [selDevices, setSelDevices] = useState<string[]>(saved.devices ?? []);
  const [selLabels, setSelLabels] = useState<string[]>(saved.labels ?? []);
  const [selTypes, setSelTypes] = useState<string[]>(saved.types ?? []);
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_KEY, JSON.stringify({ range, date, devices: selDevices, labels: selLabels, types: selTypes }));
    } catch {
      /* 私密模式等存不了：只是回來時篩選重置 */
    }
  }, [range, date, selDevices, selLabels, selTypes]);
  const [items, setItems] = useState<MyEvent[]>([]);
  const [facets, setFacets] = useState<MyEventFacets>(EMPTY_FACETS);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const genRef = useRef(0);

  // 時段：「近 N」每次重抓時以當下為終點
  const win = useMemo(
    () => rangeWindow(range, Date.now() * 1000, dayWindowInZone(date, zone)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, date, zone, reloadKey]
  );

  const query = useCallback(
    (cur?: string) =>
      fetchMyEvents({ fromUs: win.fromUs, toUs: win.toUs, devices: selDevices, labels: selLabels, types: selTypes, cursor: cur, limit: PAGE }),
    [win, selDevices, selLabels, selTypes]
  );

  // 第一頁（篩選 / 時段一變就重抓；裝置清單到了才開始）
  const devicesReady = devices !== null;
  useEffect(() => {
    if (!devicesReady) return;
    const gen = ++genRef.current;
    setLoading(true);
    setError(null);
    query()
      .then((p) => {
        if (gen !== genRef.current) return;
        setItems(p.items);
        setFacets(p.facets);
        setTotal(p.total);
        setCursor(p.next_cursor);
      })
      .catch((e: unknown) => {
        if (gen !== genRef.current) return;
        setItems([]);
        setFacets(EMPTY_FACETS);
        setTotal(0);
        setCursor('');
        setError(e instanceof ApiError && e.code === 'RANGE_TOO_LARGE' ? t('eventCenter.rangeTooLarge') : e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.networkError'));
      })
      .finally(() => {
        if (gen === genRef.current) setLoading(false);
      });
  }, [devicesReady, query, t]);

  const loadMore = () => {
    if (!cursor || loading) return;
    const gen = genRef.current;
    setLoading(true);
    query(cursor)
      .then((p) => {
        if (gen !== genRef.current) return;
        setItems((xs) => [...xs, ...p.items]);
        setCursor(p.next_cursor);
      })
      .catch(() => {
        if (gen === genRef.current) setError(t('common.networkError'));
      })
      .finally(() => {
        if (gen === genRef.current) setLoading(false);
      });
  };

  // 即時：「近 N」時段收到新事件 → 2 秒內合併重抓一次
  useEffect(() => {
    if (camIds.length === 0) return;
    ensureEventRooms(camIds);
    if (range === 'day') return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = onLiveEvent(() => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        setReloadKey((k) => k + 1);
      }, LIVE_DEBOUNCE_MS);
    });
    return () => {
      off();
      if (timer) clearTimeout(timer);
    };
  }, [camIds, range]);

  // 不通知區（docs/mute-zones.md §11-3）：沒選「在不通知區」→ 不列 muted
  const showMuted = selLabels.includes('muted');
  const shown = useMemo(() => (showMuted ? items : items.filter((e) => !isMuted(e))), [items, showMuted]);
  const labelName = (l: string) =>
    l === 'false_positive' ? t('ai.falsePositive') : l === 'uncertain' ? t('ai.uncertain') : l === 'unjudged' ? t('eventCenter.unjudged') : l === 'unconfirmed' ? t('events.unconfirmed') : l === 'muted' ? t('muteZones.filter') : t(`ai.${l}`);
  // 類型：原始 event_type 有語意字串也有 hex（例如好幾種都算「其他」）→ 依顯示的分類合併成一顆 chip，點一下選 / 取消那一類的全部原始值
  const typeGroups = useMemo(() => {
    const g = new Map<string, { label: string; keys: string[]; count: number }>();
    for (const k of new Set([...Object.keys(facets.type), ...selTypes])) {
      const c = clusterOfEvent({ event_id: '', event_type: k });
      const e = g.get(c.key) ?? { label: c.label, keys: [], count: 0 };
      e.keys.push(k);
      e.count += facets.type[k] ?? 0;
      g.set(c.key, e);
    }
    return [...g.entries()].map(([key, v]) => ({ key, ...v })).sort((a, b) => b.count - a.count);
  }, [facets.type, selTypes]);
  const toggleGroup = (keys: string[]) =>
    setSelTypes((xs) => (keys.every((k) => xs.includes(k)) ? xs.filter((x) => !keys.includes(x)) : [...new Set([...xs, ...keys])]));
  const bars = useMemo(() => hourBars(facets.hour, win), [facets.hour, win]);
  const maxBar = Math.max(1, ...bars.map((b) => b.count));
  const anyFilter = selDevices.length + selLabels.length + selTypes.length > 0;

  return (
    <section className="vms-ec">
      <header className="vms-ec-head">
        <h1 style={{ marginTop: 0 }}>{t('eventCenter.title')}</h1>
        <Link className="btn-ghost" to="/playback/multi">{t('multiPlayback.title')} →</Link>
        <Link className="btn-ghost" to="/cloud-playback">{t('recPlayback.cloudEventTitle')} →</Link>
      </header>
      <p className="text-secondary vms-ec-sub">{t('eventCenter.subtitle')}</p>

      <div className="vms-ec-filters card" data-testid="ec-filters">
        <div className="vms-ec-row">
          <span className="vms-ec-row-label text-secondary">{t('eventCenter.range')}</span>
          {(['24h', '7d', '30d', 'day'] as RangeKey[]).map((k) => (
            <button key={k} className={`evt-chip${range === k ? ' active' : ''}`} aria-pressed={range === k} onClick={() => setRange(k)} data-testid={`ec-range-${k}`}>
              {t(`eventCenter.range_${k}`)}
            </button>
          ))}
          {range === 'day' && <input className="evt-date" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label={t('eventCenter.range_day')} data-testid="ec-date" />}
        </div>

        <div className="vms-ec-row">
          <span className="vms-ec-row-label text-secondary">{t('eventCenter.cameras')}</span>
          {cameras.map((d) => (
            <button key={d.device_id} className={`evt-chip${selDevices.includes(d.device_id) ? ' active' : ''}`} aria-pressed={selDevices.includes(d.device_id)}
              onClick={() => setSelDevices((xs) => toggle(xs, d.device_id))} data-testid={`ec-dev-${d.device_id}`}>
              {deviceName(d)} <span className="vms-ec-count">{facets.device[d.device_id] ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="vms-ec-row">
          <span className="vms-ec-row-label text-secondary">{t('eventCenter.ai')}</span>
          {MY_EVENT_LABELS.map((l) => (
            <button key={l} className={`evt-chip${selLabels.includes(l) ? ' active' : ''}`} aria-pressed={selLabels.includes(l)}
              onClick={() => setSelLabels((xs) => toggle(xs, l))} data-testid={`ec-label-${l}`}>
              {labelName(l)} <span className="vms-ec-count">{facets.label[l] ?? 0}</span>
            </button>
          ))}
        </div>

        {typeGroups.length > 0 && (
          <div className="vms-ec-row">
            <span className="vms-ec-row-label text-secondary">{t('eventCenter.types')}</span>
            {typeGroups.map((g) => {
              const on = g.keys.every((k) => selTypes.includes(k));
              return (
                <button key={g.key} className={`evt-chip${on ? ' active' : ''}`} aria-pressed={on} onClick={() => toggleGroup(g.keys)} data-testid={`ec-type-${g.key}`}>
                  {g.label} <span className="vms-ec-count">{g.count}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="vms-ec-hours" role="img" aria-label={t('eventCenter.hoursLabel')} data-testid="ec-hours">
          {bars.map((b) => (
            <span key={b.fromUs} className="vms-ec-bar" style={{ height: `${(b.count / maxBar) * 100}%` }}
              title={`${formatTimestampUs(b.fromUs, { timeZone: zone })} · ${b.count}`} />
          ))}
        </div>

        <div className="vms-ec-row vms-ec-summary">
          <span data-testid="ec-total">{t('eventCenter.total', { count: total })}</span>
          {anyFilter && (
            <button className="btn-ghost" onClick={() => { setSelDevices([]); setSelLabels([]); setSelTypes([]); }} data-testid="ec-clear">
              {t('eventCenter.clear')}
            </button>
          )}
        </div>
      </div>

      {error && <p className="text-danger" role="alert">{error}</p>}
      {/* 不通知區：預設不列（篩選「在不通知區」才看）；數字用伺服器的 facet */}
      <MutedHiddenLine count={facets.label.muted ?? 0} showing={showMuted}
        onToggle={() => setSelLabels((xs) => (xs.includes('muted') ? xs.filter((x) => x !== 'muted') : ['muted']))} />
      {!error && !loading && items.length === 0 && <p className="text-secondary" data-testid="ec-empty">{t('eventCenter.empty')}</p>}

      {/* 外觀翻新（2026-10-11，C 表格寫法）：看狀態的頁面用表格，欄位對齊好掃 */}
      <div className="vms-ov-scroll card">
        <table className="vms-ov-table ec-table" data-testid="ec-list">
          <thead>
            <tr>
              <th>{t('eventCenter.colType')}</th>
              <th>{t('eventCenter.colCamera')}</th>
              <th>{t('eventCenter.colTime')}</th>
              <th>{t('eventCenter.colAi')}</th>
              <th aria-label={t('eventCenter.watch')} />
            </tr>
          </thead>
          <tbody>
            {shown.map((ev) => {
              const c = clusterOfEvent(ev);
              const ai = eventAiLine(t, ev);
              const go = () => navigate(playbackHref(ev), { state: { from: 'eventCenter' } });
              return (
                <tr key={`${ev.device_id}|${ev.event_ts}|${ev.event_id}`}>
                  <td>
                    <button className="ec-type" onClick={go} title={t('eventCenter.watch')} data-testid="ec-row">
                      <EventThumb deviceId={ev.device_id} ev={ev} cluster={c} />
                      <span className="ec-type-label">{c.label}</span>
                    </button>
                  </td>
                  <td>{nameOf.get(ev.device_id) ?? ev.device_id}</td>
                  <td className="ec-time">{formatTimestampUs(ev.event_ts, { timeZone: camZone(ev.device_id) })}</td>
                  <td>
                    {ai ? <span className={`evt-row-ai${ai.confirmed ? ' evt-row-ai-confirmed' : ''}`}>{ai.text}</span> : null}
                    {/* 驗證閘道判「未確認」：跟 AI 電腦的判斷並排（可同時有）；兩個都沒有才顯示 — */}
                    {isUnconfirmed(ev) ? <span className="evt-unconf" title={t('events.unconfirmedHint')} data-testid="ec-unconfirmed">{t('events.unconfirmed')}</span> : null}
                    {!ai && !isUnconfirmed(ev) ? <span className="text-secondary">—</span> : null}
                  </td>
                  <td className="ec-cta">
                    <button className="btn-ghost" onClick={go}>{t('eventCenter.watch')}</button>
                    <MuteRowActions deviceId={ev.device_id} ev={ev} onChanged={() => setReloadKey((k) => k + 1)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {cursor && (
        <button className="btn-ghost evt-more" onClick={loadMore} disabled={loading} data-testid="ec-more">
          {loading ? t('common.loading') : t('common.loadMore')}
        </button>
      )}
    </section>
  );
}
